import { SearchQueryModelConverter } from '@neptune/search-query-domain';
import { getEntityCustomId, getEntityShortId } from '@neptune/shared/entity-domain';
import { searchLeaderboard } from '@neptune/shared/leaderboard-domain';

import {
  KnownAttributes,
  knownAttributesForExperimentIdentification,
} from 'domain/experiment/attribute/known-attributes';

type ParentLookupResult = {
  shortId?: string;
};

export type RunLineageParentResolution =
  | {
      status: 'not-requested';
    }
  | {
      customRunId: string;
      status: 'loading';
    }
  | {
      customRunId: string;
      shortId: string;
      status: 'resolved';
    }
  | {
      customRunId: string;
      status: 'not-found';
    }
  | {
      customRunId: string;
      status: 'warning';
    };

export type RunLineageParentResolutionState = {
  effective: RunLineageParentResolution;
  requested: RunLineageParentResolution;
};

export type RunLineageParentResolutionWarningKind =
  | 'parent-not-found'
  | 'parent-verification-failed';

export type RunLineageParentResolutionWarning = {
  kind: RunLineageParentResolutionWarningKind;
  message: string;
};

type ParentsLookup = (
  projectIdentifier: string,
  customRunIds: string[],
) => Promise<Map<string, ParentLookupResult>>;

export function createRunLineageParentResolutionState({
  effectiveParentId,
  requestedParentNotFound,
  requestedParentId,
}: {
  effectiveParentId?: string;
  requestedParentNotFound?: boolean;
  requestedParentId?: string;
}): RunLineageParentResolutionState {
  return {
    effective: createPendingResolution(effectiveParentId),
    requested: createPendingResolution(requestedParentId, requestedParentNotFound),
  };
}

export async function resolveRunLineageParents(
  {
    effectiveParentId,
    projectIdentifier,
    requestedParentId,
    requestedParentNotFound = false,
  }: {
    effectiveParentId?: string;
    projectIdentifier: string;
    requestedParentId?: string;
    requestedParentNotFound?: boolean;
  },
  lookupParents: ParentsLookup = lookupRunLineageParents,
): Promise<RunLineageParentResolutionState> {
  const initialState = createRunLineageParentResolutionState({
    effectiveParentId,
    requestedParentId,
    requestedParentNotFound,
  });
  const customRunIdsToLookup = Array.from(
    new Set(
      [
        requestedParentId && !requestedParentNotFound ? requestedParentId : undefined,
        effectiveParentId,
      ].filter((customRunId): customRunId is string => customRunId !== undefined),
    ),
  );

  if (customRunIdsToLookup.length === 0) {
    return initialState;
  }

  try {
    const lookupResults = await lookupParents(projectIdentifier, customRunIdsToLookup);

    return {
      requested: resolveParentFromLookup({
        current: initialState.requested,
        customRunId: requestedParentId,
        lookupResults,
        shouldLookup: !!requestedParentId && !requestedParentNotFound,
      }),
      effective: resolveParentFromLookup({
        current: initialState.effective,
        customRunId: effectiveParentId,
        lookupResults,
        shouldLookup: !!effectiveParentId,
      }),
    };
  } catch {
    return {
      requested: createFailedResolution({
        current: initialState.requested,
        customRunId: requestedParentId,
        shouldLookup: !!requestedParentId && !requestedParentNotFound,
      }),
      effective: createFailedResolution({
        current: initialState.effective,
        customRunId: effectiveParentId,
        shouldLookup: !!effectiveParentId,
      }),
    };
  }
}

export function getRunLineageParentResolutionWarnings(
  state: RunLineageParentResolutionState,
): RunLineageParentResolutionWarning[] {
  return [
    getParentResolutionWarning('Requested parent', state.requested),
    getParentResolutionWarning('Effective parent', state.effective),
  ].filter((warning): warning is RunLineageParentResolutionWarning => warning !== undefined);
}

async function lookupRunLineageParents(
  projectIdentifier: string,
  customRunIds: string[],
): Promise<Map<string, ParentLookupResult>> {
  const result = await searchLeaderboard({
    projectIdentifier,
    type: ['run'],
    attributesToFetch: [...knownAttributesForExperimentIdentification, KnownAttributes.CustomRunId],
    experimentsOnly: false,
    pagination: {
      limit: customRunIds.length,
      offset: 0,
    },
    query: SearchQueryModelConverter.convertSearchQueryToNql({
      criteria: customRunIds.map((customRunId) => ({
        attribute: KnownAttributes.CustomRunId,
        operator: '=',
        type: 'string',
        value: customRunId,
      })),
      operator: 'or',
    }),
  });
  const lookupResults = new Map<string, ParentLookupResult>();

  for (const entity of result.entries) {
    const customRunId = getEntityCustomId(entity);
    const shortId = getEntityShortId(entity);

    if (customRunId && shortId) {
      lookupResults.set(customRunId, { shortId });
    }
  }

  return lookupResults;
}

function createPendingResolution(
  customRunId?: string,
  parentNotFound = false,
): RunLineageParentResolution {
  if (!customRunId) {
    return {
      status: 'not-requested',
    };
  }

  if (parentNotFound) {
    return {
      customRunId,
      status: 'not-found',
    };
  }

  return {
    customRunId,
    status: 'loading',
  };
}

function getParentResolutionWarning(
  label: string,
  resolution: RunLineageParentResolution,
): RunLineageParentResolutionWarning | undefined {
  if (resolution.status === 'not-found') {
    return {
      kind: 'parent-not-found',
      message: `${label} '${resolution.customRunId}' was not found in this project.`,
    };
  }

  if (resolution.status === 'warning') {
    return {
      kind: 'parent-verification-failed',
      message: `${label} '${resolution.customRunId}' could not be verified. Check permissions or retry later.`,
    };
  }

  return undefined;
}

function resolveParentFromLookup({
  current,
  customRunId,
  lookupResults,
  shouldLookup,
}: {
  current: RunLineageParentResolution;
  customRunId?: string;
  lookupResults: Map<string, ParentLookupResult>;
  shouldLookup: boolean;
}): RunLineageParentResolution {
  if (!customRunId || !shouldLookup) {
    return current;
  }

  const result = lookupResults.get(customRunId);

  if (result?.shortId) {
    return {
      customRunId,
      shortId: result.shortId,
      status: 'resolved',
    };
  }

  return {
    customRunId,
    status: 'not-found',
  };
}

function createFailedResolution({
  current,
  customRunId,
  shouldLookup,
}: {
  current: RunLineageParentResolution;
  customRunId?: string;
  shouldLookup: boolean;
}): RunLineageParentResolution {
  if (!customRunId || !shouldLookup) {
    return current;
  }

  return {
    customRunId,
    status: 'warning',
  };
}
